import React from 'react';
import { SparklineProps } from '../../types';

/**
 * Sparkline - Mini line chart with dots for weekly performance
 * Features:
 * - Filled dots for starter weeks
 * - Hollow dots for bench weeks
 * - Responsive scaling based on data range
 */
const Sparkline: React.FC<SparklineProps> = ({
  data,
  width,
  height = 24,
  color = '#3b82f6',
  strokeWidth = 1.5,
  showDots = true
}) => {
  // Use a ref to get the actual width of the container
  const svgRef = React.useRef<SVGSVGElement>(null);
  const [svgWidth, setSvgWidth] = React.useState(width || 120);

  React.useEffect(() => {
    const updateWidth = () => {
      if (svgRef.current) {
        const containerWidth = svgRef.current.parentElement?.offsetWidth || 120;
        setSvgWidth(containerWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  if (!data || data.length === 0) {
    return (
      <div className="w-full flex items-center justify-center bg-slate-800 rounded" style={{ height }}>
        <span className="text-slate-500 text-xs">No data</span>
      </div>
    );
  }

  // Filter out invalid data points and ensure finite numbers
  const validData = data.filter(d => 
    typeof d.points === 'number' && 
    !isNaN(d.points) && 
    isFinite(d.points) &&
    typeof d.week === 'number' &&
    !isNaN(d.week)
  );
  
  if (validData.length === 0) {
    return (
      <div className="w-full flex items-center justify-center bg-slate-800 rounded" style={{ height }}>
        <span className="text-slate-500 text-xs">No data</span>
      </div>
    );
  }

  const maxPoints = Math.max(...validData.map(d => d.points));
  const minPoints = Math.min(...validData.map(d => d.points));
  const range = maxPoints - minPoints || 1; // Avoid division by zero

  // Sort data by week to ensure proper line progression
  const sortedData = [...validData].sort((a, b) => a.week - b.week);

  // Calculate path for the line
  const pathData = sortedData.map((point, index) => {
    const x = (index / Math.max(sortedData.length - 1, 1)) * (svgWidth - 16) + 8; // 8px padding on each side
    const y = height - 4 - ((point.points - minPoints) / range) * (height - 8); // 4px padding top/bottom
    return { x, y, ...point };
  });

  const pathString = pathData
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');

  return (
    <svg ref={svgRef} width="100%" height={height} className="block">
      {/* Background */}
      <rect 
        width="100%" 
        height={height} 
        fill="rgb(30, 41, 59)" // slate-800
        rx={3}
      />
      
      {/* Line */}
      <path
        d={pathString}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      
      {/* Dots */}
      {showDots && pathData.map((point, index) => (
        <circle
          key={index}
          cx={point.x}
          cy={point.y}
          r={2}
          fill={point.is_starter ? color : 'transparent'}
          stroke={color}
          strokeWidth={1}
          className="transition-opacity duration-200"
        />
      ))}
      
      {/* Hover overlay for tooltip */}
      <rect
        width="100%"
        height={height}
        fill="transparent"
        className="cursor-pointer"
      >
        <title>
          {validData.length > 0 ? 
            `Weeks ${validData[0]?.week}-${validData[validData.length - 1]?.week}: ${validData.map(d => 
              `W${d.week}: ${d.points.toFixed(1)}pts${d.is_starter ? ' (started)' : ' (bench)'}`
            ).join(', ')}` :
            'No performance data'
          }
        </title>
      </rect>
    </svg>
  );
};

export default Sparkline;
